/**
 * FRONT-1737: the single, shared "a document was just saved" toast wording,
 * used by every "Save as document" / "resolve chat to document" surface
 * (the inbox chat modal's per-item save, per-response save, and resolve-to-
 * document action; the phone surface's own save-as-document action).
 *
 * A document saved out of a chat scoped to a Work container is filed there
 * automatically (`src/work/chat-scope-attach.ts`) — the backend reports
 * that back as `attachedTo` on the save response. When it did, the toast
 * names the container instead of the generic "Saved as document" line, so
 * the user learns where the file landed without having to go look. Same
 * pure-decision split as `folder-attach-outcome.ts`'s
 * `resolveFolderAttachOutcome` — extracted so the wording is one string
 * literal, not four hand-copied ones that can drift.
 */
export function resolveDocumentSavedMessage(
  docTitle: string,
  attachedTo: { name: string } | null | undefined,
): string {
  // FRONT-1737: the attach can succeed while the (best-effort, non-blocking)
  // container-name lookup that runs right after it comes back empty — the
  // backend still reports the attach, just with `name: ""`
  // (`src/work/chat-scope-attach.ts`). Naming a blank container in the toast
  // would read as broken, so an empty name falls back to the same plain
  // wording as "nothing was attached at all".
  if (attachedTo && attachedTo.name) {
    return `Saved to "${attachedTo.name}" — "${docTitle}"`;
  }
  return `Saved as document — "${docTitle}"`;
}

/**
 * FRONT-1841 adversarial-review fix: the upload twin of
 * `resolveDocumentSavedMessage` above, for a file the user drops, pastes or
 * picks straight into a scoped chat's composer (`useUploadFile`,
 * `frontend/src/hooks/queries/use-files.ts`). A dropped file has no Eddy
 * turn of its own to say "I filed that" the way a tool call does — this
 * toast is the equivalent, and it is the only place that notice reaches the
 * user. `resolveDocumentSavedMessage`'s own wording ("Saved as document…")
 * doesn't fit: an upload is never a document, and an ORDINARY unscoped
 * upload (the common case) must stay silent exactly as it does today —
 * unlike a document save, which always toasts something. So this returns
 * `null` — "say nothing" — rather than a generic fallback sentence when
 * `attachedTo` is absent; the caller only toasts when it gets a string back.
 * A present-but-empty `attachedTo.name` (the attach succeeded but the
 * best-effort container-name lookup came back blank — see
 * `attachToChatScope`'s own docblock in `src/work/chat-scope-attach.ts`)
 * still gets a toast, just without naming a blank container, mirroring how
 * `resolveDocumentSavedMessage` falls back for the identical case.
 */
export function resolveUploadFiledMessage(
  fileName: string,
  attachedTo: { name: string } | null | undefined,
): string | null {
  if (!attachedTo) return null;
  if (attachedTo.name) return `Filed on "${attachedTo.name}" — "${fileName}"`;
  return `Filed automatically — "${fileName}"`;
}
